import { useState, useEffect, useCallback, useRef } from 'react';
import { Instagram, Facebook, Linkedin, CheckCircle2, AlertTriangle, Circle, Link2, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { getApiUrl } from '../lib/apiConfig';
import { PageChoice } from './PageChoice';
import { facebookPageSection, linkedinPageSection, type SocialPage } from '../lib/pageChoice';

type PlatformKey = 'instagram' | 'facebook' | 'linkedin';

interface ConnectedAccount {
  handle?: string;
  display_name?: string;
  reauth_required?: boolean;
}

type ConnectedPlatforms = Partial<Record<PlatformKey, ConnectedAccount | ''>>;

const PLATFORMS: { key: PlatformKey; label: string; Icon: typeof Instagram }[] = [
  { key: 'instagram', label: 'Instagram', Icon: Instagram },
  { key: 'facebook', label: 'Facebook', Icon: Facebook },
  { key: 'linkedin', label: 'LinkedIn', Icon: Linkedin },
];

interface StatusInfo {
  state: 'connected' | 'reauth' | 'none';
  label: string;
}

function statusFor(acc: ConnectedAccount | '' | undefined): StatusInfo {
  if (acc && typeof acc === 'object' && acc.handle) {
    if (acc.reauth_required) {
      return { state: 'reauth', label: 'Verbinding verlopen — opnieuw koppelen' };
    }
    return { state: 'connected', label: `Gekoppeld als ${acc.handle}` };
  }
  return { state: 'none', label: 'Nog niet gekoppeld' };
}

export function SocialConnectionsPanel() {
  const { user } = useAuth();
  const [platforms, setPlatforms] = useState<ConnectedPlatforms>({});
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const mountedRef = useRef(true);
  useEffect(() => {
    // Expliciet true zetten: StrictMode draait in dev cleanup + re-setup,
    // maar refs resetten niet mee.
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const [fbPages, setFbPages] = useState<SocialPage[]>([]);
  const [fbPageId, setFbPageId] = useState<string | null>(null);
  const [fbPageName, setFbPageName] = useState<string | null>(null);
  const [fbSaving, setFbSaving] = useState(false);

  const [liPages, setLiPages] = useState<SocialPage[]>([]);
  const [liPageId, setLiPageId] = useState<string | null>(null);
  const [liPageName, setLiPageName] = useState<string | null>(null);
  const [liSaving, setLiSaving] = useState(false);

  const fbConnected = statusFor(platforms.facebook).state === 'connected';
  const liConnected = statusFor(platforms.linkedin).state === 'connected';

  const callFacebookPages = useCallback(
    async (payload: Record<string, unknown>) => {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) return null;
      const res = await fetch(getApiUrl('/functions/v1/facebook-pages'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      // Stil falen: zonder paginalijst blijft het paneel gewoon bruikbaar
      // (en blijft de keuze-UI verborgen). Dekt ook de korte fase waarin
      // de frontend live is maar de edge function nog niet.
      if (!res.ok) return null;
      return res.json();
    },
    [],
  );

  useEffect(() => {
    if (!fbConnected) return;
    let cancelled = false;
    callFacebookPages({ action: 'list' }).then((body) => {
      if (cancelled || !body?.success) return;
      setFbPages(body.pages ?? []);
      setFbPageId(body.current_page_id ?? null);
      setFbPageName(body.current_page_name ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, [fbConnected, callFacebookPages]);

  const handleSelectPage = async (pageId: string) => {
    setError(null);
    setFbSaving(true);
    const body = await callFacebookPages({ action: 'set', page_id: pageId });
    if (mountedRef.current) {
      if (body?.success) {
        setFbPageId(body.page_id);
        setFbPageName(body.page_name);
      } else {
        setError('De paginakeuze opslaan lukte niet. Probeer het zo nog eens.');
      }
      setFbSaving(false);
    }
  };

  useEffect(() => {
    if (!liConnected) return;
    let cancelled = false;
    callFacebookPages({ action: 'list', platform: 'linkedin' }).then((body) => {
      if (cancelled || !body?.success) return;
      setLiPages(body.pages ?? []);
      setLiPageId(body.current_page_id ?? null);
      setLiPageName(body.current_page_name ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, [liConnected, callFacebookPages]);

  const handleSelectLinkedinPage = async (pageId: string | null) => {
    setError(null);
    setLiSaving(true);
    const body = await callFacebookPages({
      action: 'set',
      platform: 'linkedin',
      page_id: pageId,
    });
    if (mountedRef.current) {
      if (body?.success) {
        setLiPageId(body.page_id ?? null);
        setLiPageName(body.page_name ?? null);
      } else {
        setError('De paginakeuze opslaan lukte niet. Probeer het zo nog eens.');
      }
      setLiSaving(false);
    }
  };

  const loadStatus = useCallback(async () => {
    if (!user?.id) return;
    const { data, error: qErr } = await supabase
      .from('social_profiles')
      .select('connected_platforms')
      .eq('user_id', user.id)
      .maybeSingle();
    if (!qErr && data?.connected_platforms) {
      setPlatforms(data.connected_platforms as ConnectedPlatforms);
    }
    setLoading(false);
  }, [user?.id]);

  const startPolling = useCallback(() => {
    if (pollTimer.current) clearInterval(pollTimer.current);
    let ticks = 0;
    pollTimer.current = setInterval(async () => {
      ticks += 1;
      await loadStatus();
      if (ticks >= 30 && pollTimer.current) {
        clearInterval(pollTimer.current); // stop na ~2 min
        pollTimer.current = null;
      }
    }, 4000);
  }, [loadStatus]);

  // Bij (her)laden van de tab: status laden + kort pollen, zodat een
  // gebruiker die net terugkeert van de connect-redirect de nieuwe
  // koppeling automatisch ziet verschijnen (webhook landt async).
  useEffect(() => {
    loadStatus();
    startPolling();
    return () => {
      if (pollTimer.current) clearInterval(pollTimer.current);
    };
  }, [loadStatus, startPolling]);

  const handleConnect = async () => {
    setError(null);
    setOpening(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) {
        setError('Je sessie is verlopen. Log opnieuw in.');
        return;
      }

      const res = await fetch(getApiUrl('/functions/v1/upload-post-connect'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({}),
      });
      const body = await res.json();

      if (!res.ok || !body.access_url) {
        setError(body.error || 'Het koppelen lukte even niet. Probeer het zo nog eens.');
        return;
      }

      // Zelfde tab: geen popup-blocker, niets kwijt; Upload-Post stuurt
      // automatisch terug naar de socials-tab (redirect_url).
      window.location.href = body.access_url;
    } catch {
      setError('Er ging iets mis. Probeer het zo nog eens.');
      setOpening(false);
    }
  };

  const allConnected = PLATFORMS.every(
    (p) => statusFor(platforms[p.key]).state === 'connected',
  );

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="mb-5">
        <h2 className="text-lg font-bold text-[#231F20] mb-1">Je socials koppelen</h2>
        <p className="text-sm text-gray-600">
          Dit is jouw 10%. Klik hieronder, log één keer in bij Instagram, Facebook
          en LinkedIn — daarna regelt dibbi de rest. Geen instellingen, geen gedoe.
        </p>
      </div>

      <div className="space-y-3 mb-5">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
            <Loader2 className="w-4 h-4 animate-spin" />
            Even kijken hoe je ervoor staat...
          </div>
        ) : (
          PLATFORMS.map(({ key, label, Icon }) => {
            const s = statusFor(platforms[key]);
            return (
              <div key={key} className="rounded-lg border border-gray-100 bg-gray-50">
                <div className="flex items-center gap-3 p-3">
                  <Icon className="w-5 h-5 text-gray-700 shrink-0" />
                  <span className="font-medium text-gray-900 w-24 shrink-0">{label}</span>
                  <span className="flex items-center gap-2 text-sm">
                    {s.state === 'connected' && (
                      <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />
                    )}
                    {s.state === 'reauth' && (
                      <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
                    )}
                    {s.state === 'none' && (
                      <Circle className="w-4 h-4 text-gray-300 shrink-0" />
                    )}
                    <span
                      className={
                        s.state === 'connected'
                          ? 'text-green-700'
                          : s.state === 'reauth'
                            ? 'text-amber-700'
                            : 'text-gray-500'
                      }
                    >
                      {s.label}
                    </span>
                  </span>
                </div>
                {key === 'facebook' && (
                  <PageChoice
                    section={facebookPageSection(fbConnected, fbPages.length, fbPageId)}
                    pages={fbPages}
                    currentPageId={fbPageId}
                    currentPageName={fbPageName}
                    saving={fbSaving}
                    onSelect={(pageId) => {
                      if (pageId) handleSelectPage(pageId);
                    }}
                    label="Op welke Facebook-pagina wil je posten?"
                  />
                )}
                {key === 'linkedin' && (
                  <PageChoice
                    section={linkedinPageSection(liConnected, liPages.length)}
                    pages={liPages}
                    currentPageId={liPageId}
                    currentPageName={liPageName}
                    saving={liSaving}
                    onSelect={handleSelectLinkedinPage}
                    label="Waar wil je op LinkedIn posten?"
                    defaultOptionLabel="Persoonlijk profiel"
                  />
                )}
              </div>
            );
          })
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      <button
        onClick={handleConnect}
        disabled={opening}
        className="btn-primary flex items-center gap-2"
      >
        {opening ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Bezig...
          </>
        ) : (
          <>
            <Link2 className="w-5 h-5" />
            {allConnected ? 'Koppelingen beheren' : 'Koppel mijn socials'}
          </>
        )}
      </button>

      <p className="text-xs text-gray-400 mt-3">
        Je koppelt op je eigen beveiligde pagina. Dibbi slaat geen wachtwoorden op
        en je kunt een koppeling altijd weer intrekken.
      </p>

      <details className="mt-3 text-sm">
        <summary className="cursor-pointer text-gray-600 hover:text-gray-800 select-none">
          Lukt het koppelen niet?
        </summary>
        <div className="mt-2 text-gray-600 bg-gray-50 border border-gray-100 rounded-lg p-3 space-y-1">
          <p>
            Soms blokkeert je browser de koppeling en zie je een leeg scherm.
            Twee dingen die bijna altijd helpen:
          </p>
          <p>1. Probeer het in <strong>Google Chrome</strong>.</p>
          <p>
            2. Of zet in Safari even <strong>“Voorkom cross-site tracking”</strong> uit
            (Safari → Instellingen → Privacy) en klik daarna opnieuw op de knop.
          </p>
        </div>
      </details>
    </div>
  );
}
